import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const HeroSection = () => {
  const { t } = useTranslation();
  
  return (
    <section className="pt-32 pb-20 px-4 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block bg-white/20 backdrop-blur-lg text-white px-6 py-2 rounded-full text-sm font-semibold mb-6 border border-white/30"
          >
            {t('hero.badge')}
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-5xl md:text-7xl font-extrabold text-white mb-6 leading-tight"
          >
            Registro e Proteção de Músicas Online
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl md:text-2xl text-white/90 mb-8 max-w-3xl mx-auto leading-relaxed"
          >
            Registre suas músicas com <strong className="text-yellow-300">certificados digitais</strong>, <strong className="text-yellow-300">hash criptográfico</strong> e <strong className="text-yellow-300">páginas públicas verificáveis</strong>. Um sistema completo para garantir a autoria, organizar seu catálogo e mostrar suas composições para artistas, produtores e buscadores do mundo todo.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <Link
              to="/register"
              className="bg-yellow-400 text-blue-900 px-10 py-5 rounded-xl text-xl font-bold hover:shadow-2xl hover:bg-yellow-300 transition-all transform hover:scale-105 ripple inline-flex items-center space-x-3 shadow-xl"
            >
              <span>🎵 Registrar Minha Primeira Música</span>
              <ArrowRight className="w-6 h-6" />
            </Link>
            <Link
              to="/register"
              className="bg-white/10 backdrop-blur-lg border-2 border-white text-white px-10 py-5 rounded-xl text-xl font-bold hover:bg-white hover:text-blue-900 transition-all inline-flex items-center space-x-2"
            >
              <span>Quero Proteger Minhas Composições</span>
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="flex flex-wrap justify-center gap-8 mt-12 text-base font-semibold text-white"
          >
            <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
              <CheckCircle className="w-5 h-5 text-yellow-300" />
              <span>✅ Certificado Digital Instantâneo</span>
            </div>
            <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
              <CheckCircle className="w-5 h-5 text-yellow-300" />
              <span>🔒 Hash SHA-256 Criptográfico</span>
            </div>
            <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
              <CheckCircle className="w-5 h-5 text-yellow-300" />
              <span>🌐 Página Pública Verificável</span>
            </div>
            <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
              <CheckCircle className="w-5 h-5 text-yellow-300" />
              <span>🤖 Indexado em Google e IAs</span>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-16"
        >
          <div className="bg-white rounded-3xl p-8 max-w-4xl mx-auto shadow-2xl">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">{t('hero.certificateIncludes')}</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900">{t('hero.cert1Title')}</h4>
                  <p className="text-sm text-gray-600">{t('hero.cert1Desc')}</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900">{t('hero.cert2Title')}</h4>
                  <p className="text-sm text-gray-600">{t('hero.cert2Desc')}</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900">{t('hero.cert3Title')}</h4>
                  <p className="text-sm text-gray-600">{t('hero.cert3Desc')}</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900">{t('hero.cert4Title')}</h4>
                  <p className="text-sm text-gray-600">{t('hero.cert4Desc')}</p>
                </div>
              </div>
            </div>
            <div className="mt-8 text-center">
              <p className="text-gray-600 mb-2">{t('hero.fromText')}</p>
              <p className="text-5xl font-bold text-green-600">{t('hero.free')}</p>
              <p className="text-gray-600">{t('hero.upToWorks')}</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
